import { formatLocalizedNumber } from "@payping/utilities-shared/number";

import { useAppSelector, useAppTranslation } from "@/utils";
import { bnplMerchantApis } from "@payping/api";
import { CuToman } from "@payping/icons";
import { numberToPersianWords } from "@payping/utils/number";
import {
  Button,
  Card,
  Divider,
  Flex,
  Form,
  Grid,
  Input,
  Typography,
  useMessagedAsyncFn,
} from "@payping/web-components";
import classNames from "classnames";
import { FC } from "react";

type FormInputs = {
  credit: number;
};

interface InitSettingCreditProps {
  onFinish: () => void;
}
const BnplActivationPageFormInitSettingCredit: FC<InitSettingCreditProps> = ({ onFinish }) => {
  const { t } = useAppTranslation();
  const { xl } = Grid.useBreakpoint();

  const [form] = Form.useForm<FormInputs>();
  const creditAmountWatch = Form.useWatch("credit", form);
  const contract = useAppSelector((state) => state.bnplMerchant.contract);

  const [walletCreditAmountState, setWalletCreditAmount] = useMessagedAsyncFn(
    async (contractId: number, walletCreditAmount: number) => {
      const res = await bnplMerchantApis.setWalletCreditAmount({
        contractId,
        walletCreditAmount,
      });
      onFinish();
      return res;
    },
    [],
  );

  const handleFinishForm = async (data: FormInputs) => {
    const contractId = contract?.contractId;
    if (!contractId) return;
    await setWalletCreditAmount(contractId, data.credit);
  };

  const creditAmountWords = () => {
    try {
      const result = numberToPersianWords(creditAmountWatch);
      if (typeof result === "string") {
        return result;
      }
      return null;
    } catch {
      return null;
    }
  };

  return (
    <Flex className={classNames(["tw-w-full"], { "tw-pt-8": !xl })} gap={"32px"} vertical={!xl}>
      <Flex className={classNames(["tw-w-full"], { "tw-py-8 tw-pr-8": xl, "tw-px-8": !xl })}>
        <Flex className="tw-w-full xl:tw-max-w-[488px]" vertical gap={"large"}>
          <Typography.Title level={5} className="!tw-m-0">
            {t("shop.bnpl.activation.formDrawer.steps.initSettings.credit.title")}
          </Typography.Title>
          <Flex vertical className="tw-w-full">
            {(
              t("shop.bnpl.activation.formDrawer.steps.initSettings.credit.content", {
                returnObjects: true,
              }) as string[]
            ).map((item, index) => (
              <Typography.Paragraph className="!tw-m-0" key={index}>
                {item}
              </Typography.Paragraph>
            ))}
          </Flex>
          <Card.Ant>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleFinishForm}
              initialValues={{
                credit: contract?.totalCredit ?? 0,
              }}
            >
              <Form.Item
                className="!tw-m-0"
                name={"credit"}
                label={t("shop.bnpl.activation.formDrawer.steps.initSettings.credit.form.credit.label")}
                rules={[
                  { required: true },
                  {
                    validator(_, value) {
                      if (value < 100_000) {
                        return Promise.reject(
                          new Error(
                            t("common.validations.min", {
                              name: t("shop.bnpl.activation.formDrawer.steps.initSettings.credit.form.credit.label2"),
                              min: `${formatLocalizedNumber({ value: 100_000 })} ${t("common.tomans")}`,
                            }),
                          ),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                extra={
                  creditAmountWatch <= 0 || !creditAmountWords() ? null : (
                    <div className="tw-mb-2 tw-mt-1">
                      <Flex align="center">
                        <Typography.Text>{`${creditAmountWords()}`}</Typography.Text>
                        <CuToman width={24} height={24} />
                      </Flex>
                    </div>
                  )
                }
              >
                <Input.Number
                  className="tw-w-full"
                  inputMode="numeric"
                  changeOnWheel={false}
                  controls={false}
                  separateThousandth
                  step={1000}
                  addonAfter={<CuToman width={24} height={24} />}
                />
              </Form.Item>
            </Form>
          </Card.Ant>
          {xl ? (
            <Flex>
              <Button onClick={() => form.submit()} type="primary" loading={walletCreditAmountState.loading}>
                {t("shop.bnpl.activation.formDrawer.saveAndContinue")}
              </Button>
            </Flex>
          ) : null}
        </Flex>
      </Flex>
      {xl ? <Divider className="!tw-m-0 tw-h-full" type="vertical" /> : null}
      <Flex className={classNames(["tw-min-w-[calc(288px+4rem)]"], { "tw-py-8 tw-pl-8": xl, "tw-px-8": !xl })}>
        <Card.Ant className="tw-h-fit tw-w-full tw-bg-color-bg-layout">
          <Flex vertical gap={"large"} className="tw-w-full" justify="center" align="center">
            <Typography.Text>{t("shop.bnpl.activation.formDrawer.steps.initSettings.finalCredit")}</Typography.Text>
            <Flex align="center" wrap>
              <Typography.Title level={2} className="!tw-m-0">
                {formatLocalizedNumber({ value: creditAmountWatch ?? 0 })}
              </Typography.Title>
              <CuToman width={24} height={24} />
            </Flex>
          </Flex>
        </Card.Ant>
      </Flex>
      {xl ? null : (
        <Flex className="tw-px-8" gap={"middle"}>
          <Button onClick={() => form.submit()} type="primary" loading={walletCreditAmountState.loading}>
            {t("shop.bnpl.activation.formDrawer.saveAndContinue")}
          </Button>
        </Flex>
      )}
    </Flex>
  );
};

export default BnplActivationPageFormInitSettingCredit;
